import React, { useEffect, useState } from 'react';
import { ExternalLink, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public "Recommended" section — driven by the /api/affiliate/products feed.
 *
 * The single source of truth is the admin CRUD at /admin/affiliate (backed by
 * db.affiliate_products). Add / edit / remove there and this section updates
 * everywhere it's embedded (homepage today, potential landing pages later).
 *
 * Every product link is disclosed per Google's affiliate guidance:
 *   • target="_blank"
 *   • rel="sponsored nofollow noopener noreferrer"
 *
 * A small-print disclosure sits directly above the grid.
 * Silently hides itself when the list is empty (nothing to sell).
 */
const RecommendedSection = ({
  title = 'Recommended',
  eyebrow = 'Handpicked by us',
  intro = 'Products we back — every link is affiliate-tagged, so buying via one supports paisley.org.uk.',
  single = false,       // if true → render one compact card, not the full grid
  requireImage = false, // if true → skip products that have no image_url
}) => {
  const [products, setProducts] = useState([]);
  const [state, setState] = useState('loading'); // loading | ready | empty

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/affiliate/products`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        let arr = Array.isArray(d) ? d : [];
        if (requireImage) {
          arr = arr.filter((p) => (p.image_url || '').trim() !== '');
        }
        setProducts(arr);
        setState(arr.length ? 'ready' : 'empty');
      })
      .catch(() => alive && setState('empty'));
    return () => { alive = false; };
  }, [requireImage]);

  if (state === 'empty') return null;

  const trackClick = (p) => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    try {
      window.gtag('event', 'affiliate_click', {
        product_id: p.id,
        product_name: p.name,
        transport_type: 'beacon',
      });
    } catch (_e) { /* silent */ }
  };

  // Single-item slot for post/event pages — quieter than the full grid, and
  // silently hides when there's no image (per Brian's request: no image = no
  // link). Picks the first eligible product from the catalogue so the admin's
  // ordering in /admin/affiliate controls what appears.
  if (single) {
    if (state === 'loading') return null;
    const p = products.find((x) => (x.image_url || '').trim() !== '');
    if (!p) return null;
    return (
      <section
        className="max-w-3xl mx-auto px-6 pb-16"
        data-testid="single-recommended"
      >
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold uppercase tracking-[0.14em] mb-3">
          <Sparkles size={12} /> {eyebrow}
        </div>
        <a
          href={p.affiliate_url}
          target="_blank"
          rel="sponsored nofollow noopener noreferrer"
          onClick={() => trackClick(p)}
          data-testid={`single-recommended-card-${p.id}`}
          className="group flex items-stretch gap-4 bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-amber-300 hover:shadow-lg transition-all"
        >
          <div className="relative w-32 sm:w-48 shrink-0 bg-slate-100 overflow-hidden">
            <img
              src={p.image_url}
              alt={p.name}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="flex-1 py-4 pr-5 flex flex-col justify-center">
            <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-tight">
              {p.name}
            </h3>
            {p.description && (
              <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                {p.description}
              </p>
            )}
            <span className="inline-flex items-center gap-1 mt-3 text-sm font-semibold text-amber-700 group-hover:text-amber-800">
              View <ExternalLink size={13} />
            </span>
          </div>
        </a>
        <p className="text-[11px] text-slate-400 italic mt-2">
          Affiliate link — we may earn a small commission at no extra cost to you.
        </p>
      </section>
    );
  }

  return (
    <section
      className="py-16 bg-gradient-to-b from-slate-50 to-white"
      data-testid="home-recommended"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold uppercase tracking-[0.14em]">
            <Sparkles size={12} /> {eyebrow}
          </div>
          <h2
            className="text-3xl md:text-4xl font-bold text-slate-900 mt-3"
            data-testid="home-recommended-h2"
          >
            {title}
          </h2>
          {intro && <p className="text-sm text-slate-500 mt-1 max-w-2xl">{intro}</p>}
        </div>

        {/* Affiliate disclosure — required by Google + ASA/CMA guidance. */}
        <p
          className="text-[11px] text-slate-500 italic mb-6"
          data-testid="home-recommended-disclosure"
        >
          This page contains affiliate links — we may earn a commission at no extra cost to you.
        </p>

        {state === 'loading' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-72 bg-slate-100 animate-pulse rounded-2xl" />
            ))}
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            data-testid="home-recommended-list"
          >
            {products.map((p) => (
              <article
                key={p.id}
                className="group relative flex flex-col bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-amber-300 hover:shadow-lg transition-all"
                data-testid={`recommended-card-${p.id}`}
              >
                <div className="relative aspect-[16/10] bg-slate-100 overflow-hidden">
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                      <Sparkles size={36} />
                    </div>
                  )}
                </div>

                <div className="flex-1 flex flex-col p-5">
                  <h3 className="text-base font-bold text-slate-900 leading-tight">{p.name}</h3>
                  {p.description && (
                    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed line-clamp-3">
                      {p.description}
                    </p>
                  )}

                  <a
                    href={p.affiliate_url}
                    target="_blank"
                    rel="sponsored nofollow noopener noreferrer"
                    onClick={() => trackClick(p)}
                    data-testid={`recommended-view-${p.id}`}
                    className="mt-4 inline-flex items-center justify-center gap-1.5 self-start px-4 py-2 rounded-full bg-slate-900 hover:bg-amber-500 text-white text-sm font-semibold transition-colors"
                  >
                    View <ExternalLink size={13} />
                  </a>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default RecommendedSection;
